import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../AuthContext";
import "./CustomerSupportChat.css";

const CustomerSupportChat = () => {
  const location = useLocation();
  const { currentUser, user } = useAuth();
  const activeUser = currentUser || user;
  const [isOpen, setIsOpen] = useState(false);

  // Dragging State
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const hasMovedRef = useRef(false);

  const isFoodPage =
    location.pathname.startsWith("/9troeat") ||
    location.pathname.startsWith("/food") ||
    location.pathname.startsWith("/food-vendors") ||
    location.pathname.startsWith("/restaurant") ||
    location.pathname.startsWith("/chef") ||
    location.pathname.startsWith("/store");

  const [messages, setMessages] = useState([]);

  useEffect(() => {
    setMessages([
      {
        id: 1,
        sender: "support",
        text: isFoodPage
          ? "Hello! 🍔 Welcome to 9tro Eats Support. How can we assist you with your food or restaurant order today?"
          : "Hello! 👋 Welcome to 9tro Customer Support. How can we assist you today?",
        time: "Just now",
      },
    ]);
  }, [isFoodPage]);

  const [inputText, setInputText] = useState("");

  // Pointer/Mouse/Touch Drag Handlers
  const handleDragStart = (e) => {
    setIsDragging(true);
    hasMovedRef.current = false;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    dragStartRef.current = {
      x: clientX - position.x,
      y: clientY - position.y,
    };
  };

  useEffect(() => {
    const handleDragMove = (e) => {
      if (!isDragging) return;
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;

      const deltaX = clientX - dragStartRef.current.x;
      const deltaY = clientY - dragStartRef.current.y;

      if (Math.abs(deltaX - position.x) > 4 || Math.abs(deltaY - position.y) > 4) {
        hasMovedRef.current = true;
      }

      setPosition({ x: deltaX, y: deltaY });
    };

    const handleDragEnd = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      window.addEventListener("mousemove", handleDragMove);
      window.addEventListener("mouseup", handleDragEnd);
      window.addEventListener("touchmove", handleDragMove, { passive: false });
      window.addEventListener("touchend", handleDragEnd);
    }

    return () => {
      window.removeEventListener("mousemove", handleDragMove);
      window.removeEventListener("mouseup", handleDragEnd);
      window.removeEventListener("touchmove", handleDragMove);
      window.removeEventListener("touchend", handleDragEnd);
    };
  }, [isDragging, position]);

  if (!activeUser) return null;

  const handleSend = (e) => {
    e.preventDefault();
    if (!inputText.trim()) return;

    const userMsg = {
      id: Date.now(),
      sender: "user",
      text: inputText.trim(),
      time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    };

    setMessages((prev) => [...prev, userMsg]);
    setInputText("");

    setTimeout(() => {
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          sender: "support",
          text: isFoodPage
            ? "Thank you for reaching out! A 9tro Eats support specialist is reviewing your food order details. You can also chat live on WhatsApp at +234 913 387 1930."
            : "Thank you for reaching out! A 9tro support agent is reviewing your message. You can also chat with us live on WhatsApp at +234 913 387 1930.",
          time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        },
      ]);
    }, 1000);
  };

  const handleQuickAction = (text) => {
    setInputText(text);
  };

  const openWhatsAppSupport = () => {
    const defaultMsg = isFoodPage
      ? "Hello 9tro Eats Support, I need assistance with my food order."
      : "Hello 9tro Customer Support, I need assistance.";
    const waUrl = "https://wa.me/2349133871930?text=" + encodeURIComponent(defaultMsg);
    window.open(waUrl, "_blank");
  };

  const handleTriggerClick = () => {
    if (hasMovedRef.current) return; // Ignore click if dragging occurred
    setIsOpen((prev) => !prev);
  };

  return (
    <div
      className={`floating-chat-container ${isFoodPage ? "eats-theme" : ""} ${isDragging ? "dragging" : ""}`}
      style={{
        transform: `translate3d(${position.x}px, ${position.y}px, 0)`,
        touchAction: "none",
      }}
    >
      {/* Floating Chat Window */}
      {isOpen && (
        <div className="support-chat-window animate-pop">
          {/* Draggable Header */}
          <div
            className={`chat-window-header ${isFoodPage ? "eats-header" : ""} draggable-handle`}
            onMouseDown={handleDragStart}
            onTouchStart={handleDragStart}
          >
            <div className="header-info">
              <div className="avatar-wrap">
                <span className="brand-avatar-text">{isFoodPage ? "🍔" : "9T"}</span>
                <span className="online-indicator"></span>
              </div>
              <div className="header-titles">
                <h4>{isFoodPage ? "9tro Eats Support" : "9tro Support"}</h4>
                <span className="status-text">Online • Food & Orders Support</span>
              </div>
            </div>
            <button
              className="chat-close-btn"
              onClick={() => setIsOpen(false)}
              aria-label="Close chat"
            >
              ✕
            </button>
          </div>

          <div className="chat-quick-chips">
            {isFoodPage ? (
              <>
                <button onClick={() => handleQuickAction("I need help with my food order")}>
                  🍔 Food Order
                </button>
                <button onClick={() => handleQuickAction("Where is my food delivery?")}>
                  🛵 Delivery Status
                </button>
                <button onClick={() => handleQuickAction("Report missing or incorrect food item")}>
                  🥗 Missing Item
                </button>
                <button onClick={() => handleQuickAction("Query about food payment or refund")}>
                  💳 Payment
                </button>
              </>
            ) : (
              <>
                <button onClick={() => handleQuickAction("I need help with a ride")}>
                  🚗 Ride Help
                </button>
                <button onClick={() => handleQuickAction("Issue with my delivery order")}>
                  📦 Delivery
                </button>
                <button onClick={() => handleQuickAction("Payment or refund query")}>
                  💳 Payment
                </button>
              </>
            )}
          </div>

          <div className="chat-messages-list">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`chat-bubble-wrap ${msg.sender === "user" ? "user-bubble" : "support-bubble"}`}
              >
                <div className="chat-bubble">{msg.text}</div>
                <span className="bubble-time">{msg.time}</span>
              </div>
            ))}
          </div>

          <div className="chat-whatsapp-banner" onClick={openWhatsAppSupport}>
            <span>💬 Direct Chat on WhatsApp (+234 913 387 1930)</span>
            <span className="wa-arrow">›</span>
          </div>

          <form onSubmit={handleSend} className="chat-input-form">
            <input
              type="text"
              placeholder={isFoodPage ? "Ask about your food order..." : "Type a message..."}
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
            />
            <button type="submit" className={`chat-send-btn ${isFoodPage ? "eats-btn" : ""}`}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13"></line>
                <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
              </svg>
            </button>
          </form>
        </div>
      )}

      {/* Draggable Floating Button */}
      <button
        className={`floating-chat-trigger ${isFoodPage ? "eats-trigger" : ""} ${isOpen ? "active" : ""}`}
        onClick={handleTriggerClick}
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
        aria-label="Customer support chat"
        title={isFoodPage ? "9tro Eats Support" : "Customer Support"}
      >
        {isOpen ? (
          <span className="trigger-close-icon">✕</span>
        ) : (
          <div className="trigger-icon-wrap">
            {isFoodPage ? (
              <span style={{ fontSize: "24px" }}>🍔</span>
            ) : (
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
              </svg>
            )}
            <span className="chat-notification-badge">1</span>
          </div>
        )}
      </button>
    </div>
  );
};

export default CustomerSupportChat;
